import Title from '@cohort/components-xps/components/Title';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import SecondaryButton from '@cohort/wallet/components/button/SecondaryButton';
import {InputV2} from '@cohort/wallet/components/forms/v2/Input';
import CenteredContent from '@cohort/wallet/components/layout/CenteredContent';
import {AboutBrand} from '@cohort/wallet/components/space/AboutBrand';
import {Spinner} from '@cohort/wallet/components/Spinner';
import TitleWithLeadingIcon from '@cohort/wallet/components/TitleWithLeadingIcon';
import {useSyncSpaceOwnerships} from '@cohort/wallet/hooks/api/Space';
import {useUserStore} from '@cohort/wallet/hooks/stores/user';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import SpacePage from '@cohort/wallet/layouts/space/SpacePage';
import {getMerchantSpaceRoute} from '@cohort/wallet/lib/Pages';
import {trackError} from '@cohort/wallet/lib/Tracking';
import type {UserWDto} from '@cohort/wallet-schemas/user';
import {Check, X} from '@phosphor-icons/react';
import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {match} from 'ts-pattern';

type ImportStep = 'form' | 'loading' | 'retry' | 'success';

interface DigitalAssetImportFormProps {
  user: UserWDto;
  onContinueClicked: () => void;
  handleAddressCopied: () => void;
}

const DigitalAssetImportForm: React.FC<DigitalAssetImportFormProps> = ({
  user,
  onContinueClicked,
  handleAddressCopied,
}) => {
  const [addressCopied, setAddressCopied] = useState(false);
  const {t} = useTranslation('pages', {
    keyPrefix: 'space.digitalAssetImportPage',
  });
  const {accentColor, hasDarkBg} = useThemeContext();
  const {setTransitioning} = useSpaceContext();
  const navigate = useNavigate();

  return (
    <Fragment>
      <div className="space-y-2">
        <Title
          content={t('formTitle')}
          testId="digital-asset-import-title"
          classes="text-center md:text-left"
        />
        <p
          data-testid="digital-asset-import-subtitle"
          className={classNames(
            'text-center md:text-left',
            hasDarkBg ? 'text-white/60' : 'text-dark/60'
          )}
        >
          {t('formSubtitle')}
        </p>
      </div>

      <div className="mt-6">
        <label className={classNames('w-full text-sm', hasDarkBg ? 'text-white' : 'text-black/90')}>
          {t('formRecipientAddressInputLabel')}
        </label>

        <div className="mt-1.5 flex space-x-2 md:space-x-3">
          <InputV2
            testId="digital-asset-import-input-receiver-address"
            type="text"
            name="Sender wallet address"
            accentColor={accentColor}
            placeholder="0x…"
            hasDarkBg={hasDarkBg}
            value={user.wallet.address}
            onChange={() => {}}
            className="w-4/5"
          />

          <PrimaryButton
            testId="digital-asset-import-copy-address-btn"
            type="button"
            text={addressCopied ? t('formCopiedAddressBtn') : t('formCopyAddressBtn')}
            onClick={() => {
              navigator.clipboard.writeText(user.wallet.address).catch(trackError);
              setAddressCopied(true);
              handleAddressCopied();
            }}
            className="grow"
            tracking={{namespace: 'digitalAssets.import.copyAddress'}}
          />
        </div>

        <div className="mt-3 flex w-full flex-col space-y-3 md:w-4/5">
          <PrimaryButton
            testId="digital-asset-import-continue-btn"
            type="button"
            text={t('formContinueBtn')}
            onClick={onContinueClicked}
            className="grow"
            tracking={{namespace: 'digitalAssets.import.import'}}
          />
          <SecondaryButton
            text={t('formCancelBtn')}
            onClick={() => {
              setTransitioning({active: true, enterFrom: 'right'});
              navigate(-1);
            }}
            testId="digital-asset-import-cancel-btn"
            className="grow"
            type="button"
            tracking={{namespace: 'digitalAssets.import.back'}}
          />
        </div>
      </div>
    </Fragment>
  );
};

const POLLING_INTERVAL = 10 * 1000; // 10 seconds
const POLLING_MAX_DURATION = 3 * 60 * 1000; // 3 minutes

const DigitalAssetImportPage = (): JSX.Element => {
  const [importStep, setImportStep] = useState<ImportStep>('form');
  const [hasCopiedAddress, setHasCopiedAddress] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const navigate = useNavigate();
  const {t} = useTranslation('pages', {keyPrefix: 'space.digitalAssetImportPage'});
  const user = useUserStore(store => store.user);
  const {space, triggerRefresh} = useSpaceContext();
  const {accentColor, setMerchantTheme} = useThemeContext();
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (space === undefined) {
      return;
    }
    setMerchantTheme(space.merchant);
  }, [setMerchantTheme, space]);

  useEffect(() => {
    if (isPolling) {
      timeoutRef.current = setTimeout(() => {
        setIsPolling(false);
        setImportStep('retry');
      }, POLLING_MAX_DURATION);
    }
  }, [isPolling]);

  const {data: hasReceivedDigitalAsset} = useSyncSpaceOwnerships(
    isPolling ? POLLING_INTERVAL : false,
    space !== undefined
  );

  const loadingSubtitle = useMemo(() => {
    if (hasCopiedAddress) {
      return (
        <div className="space-y-2 break-words">
          <p data-testid="digital-asset-import-input-receiver-address">
            <span className="break-all">{user?.wallet.address}</span> {t('loadingAddressCopied')}
          </p>
          <p>{t('loadingSubtitle')}</p>
        </div>
      );
    }
    return t('loadingSubtitle');
  }, [hasCopiedAddress, t, user?.wallet.address]);

  if (!user) {
    return <Fragment />;
  }

  if (isPolling) {
    if (hasReceivedDigitalAsset === undefined) {
      // This can happen if the user navigates away from the page.
      clearTimeout(timeoutRef.current);
      setIsPolling(false);
    } else if (hasReceivedDigitalAsset.newOwnershipsFound) {
      clearTimeout(timeoutRef.current);
      setIsPolling(false);
      setImportStep('success');
    }
  }

  const pageContent = match(importStep)
    .with('form', () => (
      <DigitalAssetImportForm
        user={user}
        onContinueClicked={() => {
          if (space === undefined) {
            return;
          }

          setImportStep('loading');
          setIsPolling(true);
        }}
        handleAddressCopied={() => setHasCopiedAddress(true)}
      />
    ))
    .with('loading', () => (
      <TitleWithLeadingIcon
        testId="digital-asset-import-in-progress"
        title={t('loadingTitle')}
        subtitle={loadingSubtitle}
        icon={<Spinner color={accentColor} />}
      >
        <PrimaryButton
          data-testid="digital-asset-import-in-progress-cta"
          onClick={() => {
            setImportStep('form');
            setHasCopiedAddress(false);
          }}
          className="w-full md:w-1/3"
          text={t('loadingBackBtn')}
          type="submit"
          tracking={{namespace: 'digitalAssets.import.back'}}
        />
      </TitleWithLeadingIcon>
    ))
    .with('retry', () => (
      <TitleWithLeadingIcon
        testId="digital-asset-import-retry"
        title={t('retryTitle')}
        subtitle={t('retrySubtitle')}
        icon={<X className="h-11 w-11" style={{color: accentColor}} />}
      >
        <PrimaryButton
          data-testid="digital-asset-import-retry-cta"
          onClick={() => {
            setImportStep('form');
          }}
          className="w-full min-w-[25%] md:w-1/3"
          text={t('retryBtn')}
          tracking={{namespace: 'digitalAssets.import.retry'}}
        />
      </TitleWithLeadingIcon>
    ))
    .with('success', () => (
      <TitleWithLeadingIcon
        testId="digital-asset-import-successful"
        title={t('successTitle')}
        subtitle={t('successSubtitle')}
        icon={<Check className="h-11 w-11" style={{color: accentColor}} />}
      >
        <PrimaryButton
          data-testid="digital-asset-import-successful-cta"
          onClick={async () => {
            await triggerRefresh();
            navigate(getMerchantSpaceRoute());
          }}
          className="min-w-[25%]"
          text={t('successBtn')}
          tracking={{namespace: 'digitalAssets.import.success'}}
        />
      </TitleWithLeadingIcon>
    ))
    .exhaustive();

  return (
    <SpacePage
      title={t('title')}
      tracking={{
        namespace: 'digitalAssets.import',
      }}
      leftPanel={<AboutBrand />}
    >
      <CenteredContent>{pageContent}</CenteredContent>
    </SpacePage>
  );
};

export default DigitalAssetImportPage;
